import React, { useEffect } from 'react';
import { Margin, Strong } from '@ovotech/element-v5';
import { StyledHeading, StyledP } from './Cancel.styled';
import { PlanDetails } from '@monovo/ev-anytime';

export const isKiaOrHyundai = (partnership?: string | null) => {
  const p = partnership?.toUpperCase();
  return p === 'KIA' || p === 'HYUNDAI';
};

export const isOemPlan = (plan: PlanDetails) => {
  return plan.id === 'paym-oem-tier-one';
};

export const isOemJourney = (
  plan: PlanDetails,
  partnership?: string | null,
) => {
  return isOemPlan(plan) && isKiaOrHyundai(partnership);
};

export const getFormattedPlanTitle = (
  plan: PlanDetails,
  partnership?: string | null,
  includeYourPrefix = false,
) => {
  if (isOemJourney(plan, partnership)) {
    return `${includeYourPrefix ? 'your ' : ''}${partnership} partner plan`;
  }
  return plan.productType === 'payg' ? plan.title.toLowerCase() : plan.title;
};

export const PaydActiveConfirmation = ({
  partnership,
}: {
  partnership?: string | null;
}) => {
  const isOemPartner = isKiaOrHyundai(partnership);
  return (
    <>
      <StyledHeading>You’re cancelling pay as you go</StyledHeading>
      <Margin vertical={4}>
        <StyledP>
          Remember, from tomorrow you’ll be on your usual home tariff rate.
        </StyledP>
      </Margin>
      <StyledP>
        Try{' '}
        {isOemPartner
          ? `our ${partnership} monthly plan`
          : 'one of our monthly plans'}{' '}
        for predictable charging costs and great perks.
      </StyledP>
    </>
  );
};

export const MonthlyActiveConfirmation = ({
  currentPlan,
  partnership,
}: {
  currentPlan: PlanDetails;
  partnership?: string | null;
}) => {
  const currentPlanTitle = getFormattedPlanTitle(
    currentPlan,
    partnership,
    isOemPlan(currentPlan),
  );

  return (
    <>
      <StyledHeading>You’re cancelling {currentPlanTitle}</StyledHeading>
      <Margin vertical={4}>
        <StyledP>
          Remember, starting next month you’ll be charged your usual rate.{' '}
          {!isOemJourney(currentPlan, partnership) &&
            'If you cancel, any plan perks will also be cancelled.'}
        </StyledP>
      </Margin>
      <StyledP>
        Switch to <Strong>pay as you go</Strong> and you can use it as much or
        as little as you like, no fee, no commitment.
      </StyledP>
    </>
  );
};

export const InitializedConfirmation = ({
  futurePlan,
  partnership,
}: {
  futurePlan: PlanDetails;
  partnership?: string | null;
}) => {
  const futurePlanTitle = getFormattedPlanTitle(
    futurePlan,
    partnership,
    isOemPlan(futurePlan),
  );
  const isOemPartner = isKiaOrHyundai(partnership);

  return (
    <>
      <StyledHeading>You’re cancelling {futurePlanTitle}</StyledHeading>
      <Margin>
        <StyledP>
          Remember, if you want to charge for less than your home tariff rate,
          set up smart charging. Just activate{' '}
          <Strong>{futurePlanTitle}</Strong>{' '}
          {isOemPartner ? `on the ${partnership} app` : 'on the app'}.
        </StyledP>
      </Margin>
    </>
  );
};

export const SwitchingConfirmation = ({
  currentPlan,
  futurePlan,
  partnership,
}: {
  currentPlan: PlanDetails;
  futurePlan: PlanDetails;
  partnership?: string | null;
}) => {
  useEffect(() => {
    document.title = 'Cancel switch';
  }, []);

  const currentPlanTitle = getFormattedPlanTitle(
    currentPlan,
    partnership,
    false,
  );
  const futurePlanTitle = getFormattedPlanTitle(futurePlan, partnership, false);

  return (
    <>
      <StyledHeading>You’re cancelling {futurePlanTitle}</StyledHeading>
      <StyledP>
        Remember, as soon as you cancel the switch you’ll go back to your
        original plan, <Strong>{currentPlanTitle}</Strong>.
      </StyledP>
    </>
  );
};
